import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { 
  Radar, 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { 
  ArrowLeft, 
  GitCompare,
  Home,
  UserPlus,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  Shield,
  Crosshair,
  Hand,
  Sparkles,
  Crown,
  User
} from "lucide-react";

const SKILLS = [
  { key: "ataque", label: "Ataque", icon: Zap },
  { key: "passe", label: "Passe", icon: Target },
  { key: "defesa", label: "Defesa", icon: Shield },
  { key: "saque", label: "Saque", icon: Crosshair },
  { key: "bloqueio", label: "Bloqueio", icon: Hand },
  { key: "toque", label: "Toque", icon: Target },
  { key: "skill", label: "Skill", icon: Sparkles },
];

const RADAR_TICK = { fill: '#a1a1aa', fontSize: 12 };
const RADAR_RADIUS_TICK = { fill: '#a1a1aa' };
const RADAR_DOMAIN = [0, 10];

export default function ComparePage({ evaluator, onLogout }) {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [averages, setAverages] = useState([]);
  const [player1Id, setPlayer1Id] = useState("");
  const [player2Id, setPlayer2Id] = useState("");
  const [comparison, setComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [playersRes, averagesRes] = await Promise.all([
        axios.get(`${API}/players`),
        axios.get(`${API}/players/all/averages`)
      ]);
      setPlayers(playersRes.data);
      setAverages(averagesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!player1Id || !player2Id) {
      toast.error("Selecione dois jogadores para comparar");
      return;
    }
    if (player1Id === player2Id) {
      toast.error("Selecione jogadores diferentes");
      return;
    }

    try {
      const response = await axios.get(`${API}/players/compare/${player1Id}/${player2Id}`);
      setComparison(response.data);
    } catch (error) {
      toast.error("Erro ao comparar jogadores");
    }
  };

  const getOverallAverage = (avg) => {
    if (!avg?.averages) return 0;
    const values = Object.values(avg.averages);
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-400";
    if (score >= 5) return "text-yellow-400";
    return "text-red-400";
  };

  const getComparisonIcon = (val1, val2) => {
    if (val1 > val2) return <TrendingUp className="h-4 w-4 text-green-400" />;
    if (val1 < val2) return <TrendingDown className="h-4 w-4 text-red-400" />;
    return <Minus className="h-4 w-4 text-yellow-400" />;
  };

  const getPlayerName = (player) => {
    if (!player) return '';
    return player.player_name || `${player.player_first_name || ''} ${player.player_last_name || ''}`.trim();
  };

  const getWinner = () => {
    if (!comparison) return null;
    const avg1 = parseFloat(getOverallAverage(comparison.player1));
    const avg2 = parseFloat(getOverallAverage(comparison.player2));
    if (avg1 > avg2) return 1;
    if (avg2 > avg1) return 2;
    return 0;
  };

  const radarData = comparison ? SKILLS.map(skill => ({
    skill: skill.label,
    [getPlayerName(comparison.player1)]: comparison.player1.averages?.[skill.key] || 0,
    [getPlayerName(comparison.player2)]: comparison.player2.averages?.[skill.key] || 0,
  })) : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight">
              Comparar Jogadores
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        {/* Player Selection */}
        <Card className="bg-card border-border/50 shadow-xl mb-8">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-primary" />
              Selecione os Jogadores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Jogador 1
                </Label>
                <Select value={player1Id} onValueChange={setPlayer1Id}>
                  <SelectTrigger className="bg-input/50" data-testid="player1-select">
                    <SelectValue placeholder="Selecione o jogador 1" />
                  </SelectTrigger>
                  <SelectContent>
                    {averages.filter(a => a.total_evaluations > 0).map((avg) => (
                      <SelectItem key={avg.player_id} value={avg.player_id}>
                        {avg.player_yankee && <span className="font-mono mr-2">{avg.player_yankee}</span>}
                        {getPlayerName(avg)} - {avg.player_position}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-center">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold text-xl">VS</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Jogador 2
                </Label>
                <Select value={player2Id} onValueChange={setPlayer2Id}>
                  <SelectTrigger className="bg-input/50" data-testid="player2-select">
                    <SelectValue placeholder="Selecione o jogador 2" />
                  </SelectTrigger>
                  <SelectContent>
                    {averages.filter(a => a.total_evaluations > 0).map((avg) => (
                      <SelectItem key={avg.player_id} value={avg.player_id}>
                        {avg.player_yankee && <span className="font-mono mr-2">{avg.player_yankee}</span>}
                        {getPlayerName(avg)} - {avg.player_position}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <Button
                onClick={handleCompare}
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,122,255,0.3)] font-bold uppercase tracking-wider px-8"
                disabled={!player1Id || !player2Id}
                data-testid="compare-btn"
              >
                <GitCompare className="mr-2 h-4 w-4" />
                Comparar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Comparison Result */}
        {comparison && (
          <div className="space-y-6">
            {/* Radar Chart */}
            <Card className="bg-card border-border/50 shadow-xl" data-testid="radar-chart-card">
              <CardHeader>
                <CardTitle className="text-xl font-bold tracking-tight">
                  Gráfico Radar - Comparativo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#27272A" />
                      <PolarAngleAxis dataKey="skill" tick={RADAR_TICK} />
                      <PolarRadiusAxis angle={30} domain={RADAR_DOMAIN} tick={RADAR_RADIUS_TICK} />
                      <Radar
                        name={getPlayerName(comparison.player1)}
                        dataKey={getPlayerName(comparison.player1)}
                        stroke="#007AFF"
                        fill="#007AFF"
                        fillOpacity={0.3}
                      />
                      <Radar
                        name={getPlayerName(comparison.player2)}
                        dataKey={getPlayerName(comparison.player2)}
                        stroke="#CCFF00"
                        fill="#CCFF00"
                        fillOpacity={0.3}
                      />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Player Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Player 1 Card */}
              <Card className={`bg-card border-border/50 shadow-xl ${getWinner() === 1 ? 'ring-2 ring-yellow-400' : ''}`} data-testid="player1-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getWinner() === 1 && <Crown className="h-6 w-6 text-yellow-400" />}
                      {comparison.player1.player_photo_url ? (
                        <img 
                          src={comparison.player1.player_photo_url} 
                          alt={getPlayerName(comparison.player1)}
                          className="w-12 h-12 rounded-full object-cover border-2 border-primary"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-6 w-6 text-primary" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-xl font-bold">
                          {getPlayerName(comparison.player1)}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {comparison.player1.player_yankee && (
                            <Badge variant="secondary" className="font-mono">
                              {comparison.player1.player_yankee}
                            </Badge>
                          )}
                          <Badge variant="outline">{comparison.player1.player_position}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className={`text-4xl font-black ${getScoreColor(parseFloat(getOverallAverage(comparison.player1)))}`}>
                      {getOverallAverage(comparison.player1)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {comparison.player1.total_evaluations} avaliações
                  </p>
                  <div className="space-y-3">
                    {SKILLS.map((skill) => {
                      const Icon = skill.icon;
                      const val1 = comparison.player1.averages?.[skill.key] || 0;
                      const val2 = comparison.player2.averages?.[skill.key] || 0;
                      return (
                        <div key={skill.key} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{skill.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {getComparisonIcon(val1, val2)}
                            <span className={`font-bold ${getScoreColor(val1)}`}>{val1}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Player 2 Card */}
              <Card className={`bg-card border-border/50 shadow-xl ${getWinner() === 2 ? 'ring-2 ring-yellow-400' : ''}`} data-testid="player2-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getWinner() === 2 && <Crown className="h-6 w-6 text-yellow-400" />}
                      {comparison.player2.player_photo_url ? (
                        <img 
                          src={comparison.player2.player_photo_url} 
                          alt={getPlayerName(comparison.player2)}
                          className="w-12 h-12 rounded-full object-cover border-2 border-accent"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                          <User className="h-6 w-6 text-accent" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-xl font-bold">
                          {getPlayerName(comparison.player2)}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {comparison.player2.player_yankee && (
                            <Badge variant="secondary" className="font-mono">
                              {comparison.player2.player_yankee}
                            </Badge>
                          )}
                          <Badge variant="outline">{comparison.player2.player_position}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className={`text-4xl font-black ${getScoreColor(parseFloat(getOverallAverage(comparison.player2)))}`}>
                      {getOverallAverage(comparison.player2)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {comparison.player2.total_evaluations} avaliações
                  </p>
                  <div className="space-y-3">
                    {SKILLS.map((skill) => {
                      const Icon = skill.icon;
                      const val1 = comparison.player1.averages?.[skill.key] || 0;
                      const val2 = comparison.player2.averages?.[skill.key] || 0;
                      return (
                        <div key={skill.key} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{skill.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {getComparisonIcon(val2, val1)}
                            <span className={`font-bold ${getScoreColor(val2)}`}>{val2}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!comparison && !isLoading && (
          <div className="text-center py-12 text-muted-foreground">
            <GitCompare className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p>Selecione dois jogadores acima para comparar suas habilidades</p>
          </div>
        )}
      </main>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 h-auto py-2"
          data-testid="mobile-nav-home"
        >
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/players')}
          className="flex flex-col items-center gap-1 h-auto py-2"
          data-testid="mobile-nav-players"
        >
          <UserPlus className="h-5 w-5" />
          <span className="text-xs">Jogadores</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/compare')}
          className="flex flex-col items-center gap-1 h-auto py-2 text-primary"
          data-testid="mobile-nav-compare"
        >
          <GitCompare className="h-5 w-5" />
          <span className="text-xs">Comparar</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/evaluate')}
          className="flex flex-col items-center gap-1 h-auto py-2"
          data-testid="mobile-nav-evaluate"
        >
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
